import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  createQuestionBankQuestion,
  getQuestionBank,
  getQuestionBankQuestion,
  updateQuestionBankQuestion,
} from "apis/question-bank.api";
import styles from "./question-bank.page.module.scss";

const TYPE_OPTIONS = [
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "ranking", label: "Ranking" },
  { value: "acknowledgement", label: "Short Field" },
];

const emptyForm = () => ({
  type: "multiple_choice",
  prompt: "",
  optionsText: "Option 1\nOption 2",
});

const toForm = (question) => ({
  type: question?.type || "multiple_choice",
  prompt: question?.prompt || "",
  optionsText: Array.isArray(question?.options) ? question.options.join("\n") : "",
});

const readError = (error) => {
  const code = error?.data?.code || error?.data?.errorCode;
  if (code === "QB1001") return "This Question Bank item no longer exists. Refresh the list.";
  if (code === "QB1002") {
    const duplicateCode = error?.data?.codeName || error?.data?.questionCode || "another question";
    return `This wording already exists as ${duplicateCode}.`;
  }
  if (code === "QB1003")
    return "Multiple Choice and Ranking need at least two options. Short Field has no options.";
  if (code === "QB1004") return "Options must not be blank or duplicated.";
  return error?.data?.message || "Unable to save this Question Bank item.";
};

const QuestionBankPage = () => {
  const history = useHistory();
  const { bankQuestionId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getQuestionBank();
      setQuestions(Array.isArray(rows) ? rows : []);
    } catch (loadError) {
      setError("Unable to load the Question Bank.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  useEffect(() => {
    if (!feedback) return undefined;
    const timeout = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(timeout);
  }, [feedback]);

  useEffect(() => {
    let mounted = true;
    if (!bankQuestionId) {
      setSelected(null);
      setForm(emptyForm());
      return undefined;
    }

    setLoading(true);
    getQuestionBankQuestion({ bankQuestionId })
      .then((question) => {
        if (!mounted) return;
        setSelected(question);
        setForm(toForm(question));
        setEditorOpen(true);
        setError(null);
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(readError(loadError));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [bankQuestionId]);

  const visibleQuestions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return questions;
    return questions.filter((question) =>
      [question?.code, question?.prompt, ...(question?.options || [])]
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [questions, search]);

  const activeUsageCount = (selected?.usage || []).filter(
    (usage) => String(usage?.status || "").toLowerCase() === "active",
  ).length;

  const nextQuestionCode = useMemo(() => {
    const highest = questions.reduce((maximum, question) => {
      const match = String(question?.code || "").match(/^CQ-(\d+)$/i);
      return match ? Math.max(maximum, Number(match[1])) : maximum;
    }, 0);
    return `CQ-${String(highest + 1).padStart(3, "0")}`;
  }, [questions]);

  const startNew = () => {
    history.push("/question-bank");
    setSelected(null);
    setForm(emptyForm());
    setEditorOpen(true);
    setError(null);
    setFeedback(null);
  };

  const selectQuestion = (question) => {
    history.push(`/question-bank/${encodeURIComponent(question.bankQuestionId)}`);
    setSelected(question);
    setForm(toForm(question));
    setEditorOpen(true);
    setError(null);
    setFeedback(null);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    if (bankQuestionId) history.push("/question-bank");
  };

  const setOptionAt = (index, value) => {
    const options = form.optionsText.split("\n");
    options[index] = value;
    setForm((current) => ({ ...current, optionsText: options.join("\n") }));
  };

  const addOption = () => {
    const options = form.optionsText ? form.optionsText.split("\n") : [];
    options.push("");
    setForm((current) => ({ ...current, optionsText: options.join("\n") }));
  };

  const removeOption = (index) => {
    const options = form.optionsText.split("\n").filter((_, optionIndex) => optionIndex !== index);
    setForm((current) => ({ ...current, optionsText: options.join("\n") }));
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (saving) return;
    const rawOptions = form.optionsText.split("\n").map((option) => option.trim());
    const options = form.type === "acknowledgement" ? [] : rawOptions.filter(Boolean);
    if (!form.prompt.trim()) {
      setError("Enter the question wording.");
      return;
    }
    if (
      form.type !== "acknowledgement" &&
      (options.length < 2 || options.length !== rawOptions.length)
    ) {
      setError("Add at least two non-empty options.");
      return;
    }
    if (new Set(options.map((option) => option.toLowerCase())).size !== options.length) {
      setError("Options must be unique.");
      return;
    }
    const payload = { type: form.type, prompt: form.prompt.trim(), options };

    setSaving(true);
    setError(null);
    setFeedback(null);
    try {
      if (selected?.bankQuestionId) {
        const response = await updateQuestionBankQuestion({
          bankQuestionId: selected.bankQuestionId,
          payload,
        });
        setFeedback(
          `Question updated in the question bank. Updated ${response?.updatedCampaigns?.length ?? activeUsageCount} active campaigns.`,
        );
      } else {
        const createdQuestion = await createQuestionBankQuestion({ payload });
        setFeedback(`${createdQuestion?.code || "Question"} added to the Question Bank.`);
      }
      setEditorOpen(false);
      setSelected(null);
      setForm(emptyForm());
      history.replace("/question-bank");
      await loadQuestions();
    } catch (saveError) {
      setError(readError(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <div>
            <h1>
              <span className={styles.titleIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h10" />
                  <circle cx="19" cy="18" r="2" />
                </svg>
              </span>
              Question Bank
            </h1>
            <p>
              Canned questions keep one Question ID in every campaign that uses them, so answers can
              be compared across campaigns. Edits here update every active campaign using the
              question.
            </p>
          </div>
        </div>
        <button type="button" className={styles.primaryButton} onClick={startNew}>
          <span aria-hidden="true">+</span> New question
        </button>
      </header>

      {error && !editorOpen ? <div className={styles.error}>{error}</div> : null}
      {feedback ? (
        <div className={styles.toast} role="status" aria-live="polite">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m5 12.5 4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{feedback}</span>
        </div>
      ) : null}

      <div className={styles.listToolbar}>
        <label className={styles.searchWrap}>
          <span aria-hidden="true">⌕</span>
          <input
            className={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search questions or IDs…"
            aria-label="Search Question Bank"
          />
        </label>
        <span className={styles.questionCount}>{questions.length} questions</span>
      </div>

      <section className={styles.questionList} aria-label="Question Bank questions">
        {loading ? <div className={styles.empty}>Loading questions…</div> : null}
        {!loading && visibleQuestions.length === 0 ? (
          <div className={styles.empty}>No questions found.</div>
        ) : null}
        {visibleQuestions.map((question) => {
          const options = (question.options || [])
            .map((option) => (typeof option === "string" ? option : option?.label || ""))
            .filter(Boolean);
          const usage = (question.usage || []).map((entry) => entry.campaignCode).filter(Boolean);
          const typeLabel =
            TYPE_OPTIONS.find((entry) => entry.value === question.type)?.label || question.type;
          const updatedAt = question.updatedAt
            ? new Date(question.updatedAt).toLocaleDateString()
            : "";
          return (
            <article key={question.bankQuestionId} className={styles.questionRow}>
              <div className={styles.questionRowMain}>
                <strong className={styles.questionCode}>{question.code}</strong>
                <p className={styles.questionPrompt}>{question.prompt}</p>
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={() => selectQuestion(question)}
                >
                  Edit
                </button>
              </div>
              <div className={styles.questionRowMeta}>
                <span className={`${styles.typeBadge} ${styles[`type${question.type}`]}`}>
                  {typeLabel}
                </span>
                {options.length ? (
                  <span className={styles.optionPreview}>
                    {options.slice(0, 3).join(" · ")}
                    {options.length > 3 ? ` · +${options.length - 3} more` : ""}
                  </span>
                ) : null}
                {usage.length ? (
                  <span className={styles.usagePreview}>
                    Used in <strong>{usage.join(", ")}</strong>
                  </span>
                ) : null}
                {updatedAt ? <span className={styles.updatedAt}>Updated {updatedAt}</span> : null}
              </div>
            </article>
          );
        })}
      </section>

      {editorOpen ? (
        <div
          className={styles.modalBackdrop}
          onMouseDown={(event) => event.target === event.currentTarget && closeEditor()}
        >
          <form
            className={styles.editorModal}
            onSubmit={onSave}
            role="dialog"
            aria-modal="true"
            aria-labelledby="question-editor-title"
          >
            <header className={styles.editorHeader}>
              <h2 id="question-editor-title">{selected ? "Edit question" : "New question"}</h2>
              <p>
                Set the wording, answer type and options. Routing is set per campaign in the survey
                builder.
              </p>
            </header>
            {error ? <div className={styles.error}>{error}</div> : null}
            <div className={styles.questionIdRow}>
              <span>Question ID</span>
              <strong>{selected?.code || nextQuestionCode}</strong>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="bank-prompt">
                Question
              </label>
              <textarea
                id="bank-prompt"
                className={styles.prompt}
                value={form.prompt}
                onChange={(event) => {
                  const prompt = event.target.value;
                  setForm((current) => ({ ...current, prompt }));
                }}
                placeholder="Type the question as the enumerator will read it…"
                required
                rows={3}
                autoFocus
              />
            </div>
            <fieldset className={styles.answerTypeFieldset}>
              <legend className={styles.fieldLabel}>Answer type</legend>
              <div className={styles.answerTypeSegment}>
                {TYPE_OPTIONS.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={
                      form.type === type.value ? styles.answerTypeActive : styles.answerTypeButton
                    }
                    onClick={() => setForm((current) => ({ ...current, type: type.value }))}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </fieldset>
            {form.type !== "acknowledgement" ? (
              <div className={styles.optionEditor}>
                <span className={styles.fieldLabel}>
                  {form.type === "ranking" ? "Items to rank" : "Options"}
                </span>
                {form.optionsText.split("\n").map((option, index) => (
                  <div key={`option-${index}`} className={styles.optionEditRow}>
                    <input
                      className={styles.input}
                      value={option}
                      onChange={(event) => setOptionAt(index, event.target.value)}
                      placeholder={`${form.type === "ranking" ? "Item" : "Option"} ${index + 1}`}
                    />
                    <button
                      type="button"
                      className={styles.removeOptionButton}
                      onClick={() => removeOption(index)}
                      disabled={form.optionsText.split("\n").length <= 2}
                      aria-label={`Remove ${form.type === "ranking" ? "item" : "option"} ${index + 1}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className={styles.addOptionButton} onClick={addOption}>
                  + Add {form.type === "ranking" ? "item" : "option"}
                </button>
              </div>
            ) : null}
            {selected?.usage?.length ? (
              <div className={styles.modalUsage}>
                Used in {selected.usage.filter((usage) => usage.status === "active").length} active
                campaigns
              </div>
            ) : null}
            <footer className={styles.editorActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={closeEditor}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className={styles.saveButton} disabled={saving}>
                {saving
                  ? "Saving…"
                  : selected
                    ? `Save & update ${activeUsageCount} campaigns`
                    : "Save question"}
              </button>
            </footer>
          </form>
        </div>
      ) : null}
    </main>
  );
};

export default QuestionBankPage;
